// TUT-512 — o perfil do escritório na tela: as máscaras, a mesma conferência
// da API (`apps/api/src/domain/perfil-do-escritorio.ts`) para avisar na hora,
// e as linhas que a prévia junta. Quem decide é o servidor; aqui é o aviso.
import { isValidCnpj } from "./registry-identity";
import type { EnderecoDoPerfil, EntradaDoPerfil, PerfilDoEscritorio } from "./types";

export const COR_DE_DESTAQUE_PADRAO = "#00426B";

const so = (valor: string) => valor.replace(/\D/gu, "");

export function mascaraDeCnpj(valor: string): string {
  return so(valor)
    .slice(0, 14)
    .replace(/^(\d{2})(\d)/u, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/u, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/u, ".$1/$2")
    .replace(/(\/\d{4})(\d)/u, "$1-$2");
}

export function mascaraDeTelefone(valor: string): string {
  const d = so(valor).slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  return d.length === 11
    ? `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`
    : `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
}

export function mascaraDeCep(valor: string): string {
  const d = so(valor).slice(0, 8);
  return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
}

/** O perfil gravado como a tela o edita (máscaras aplicadas, nulo vira vazio). */
export function entradaDoPerfil(perfil: PerfilDoEscritorio): EntradaDoPerfil {
  const endereco = perfil.address;
  return {
    displayName: perfil.displayName,
    legalName: perfil.legalName ?? "",
    cnpj: perfil.cnpj ? mascaraDeCnpj(perfil.cnpj) : "",
    accentColor: perfil.accentColor,
    contactEmail: perfil.contactEmail ?? "",
    phone: perfil.phone ? mascaraDeTelefone(perfil.phone) : "",
    website: perfil.website ?? "",
    address: {
      postalCode: endereco.postalCode ? mascaraDeCep(endereco.postalCode) : "",
      street: endereco.street ?? "",
      number: endereco.number ?? "",
      complement: endereco.complement ?? "",
      district: endereco.district ?? "",
      city: endereco.city ?? "",
      state: endereco.state ?? "",
    },
    emailSignature: perfil.emailSignature ?? "",
  };
}

export type CampoDoPerfil =
  | Exclude<keyof EntradaDoPerfil, "address">
  | `address.${keyof EnderecoDoPerfil}`;

/** A mesma regra da API, campo a campo: o primeiro problema de cada um. */
export function errosDoPerfil(entrada: EntradaDoPerfil): Partial<Record<CampoDoPerfil, string>> {
  const erros: Partial<Record<CampoDoPerfil, string>> = {};
  const nome = entrada.displayName.trim();
  if (nome.length < 2 || nome.length > 120) erros.displayName = "De 2 a 120 caracteres.";
  else if (/https?:\/\//iu.test(nome)) erros.displayName = "Sem endereço de site.";
  for (const [campo, valor] of [
    ["displayName", entrada.displayName],
    ["legalName", entrada.legalName],
    ["address.street", entrada.address.street],
    ["address.complement", entrada.address.complement],
    ["address.district", entrada.address.district],
    ["address.city", entrada.address.city],
    ["emailSignature", entrada.emailSignature],
    // Rodada de conserto 1 (Minor 6): os três que o servidor também confere
    // (`texto()` em `domain/perfil-do-escritorio.ts`) e a tela deixava passar.
    ["contactEmail", entrada.contactEmail],
    ["website", entrada.website],
    ["address.number", entrada.address.number],
  ] as const) {
    if (/[<>]/u.test(valor) && !erros[campo]) erros[campo] = "Sem os sinais < e >.";
  }
  if (entrada.legalName.trim().length > 160) erros.legalName = "Até 160 caracteres.";
  if (entrada.cnpj.trim() && !isValidCnpj(entrada.cnpj)) erros.cnpj = "CNPJ inválido: confira os dígitos.";
  if (!/^#[0-9A-Fa-f]{6}$/u.test(entrada.accentColor)) erros.accentColor = "No formato #RRGGBB.";
  const email = entrada.contactEmail.trim();
  if (email && !erros.contactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/u.test(email)) erros.contactEmail = "E-mail inválido.";
  const telefone = so(entrada.phone);
  if (telefone && telefone.length !== 10 && telefone.length !== 11) erros.phone = "Com DDD: 10 ou 11 dígitos.";
  const site = entrada.website.trim();
  if (site && !erros.website && !/^https:\/\/\S+\.\S+$/u.test(site)) erros.website = "Começa com https://.";
  const cep = so(entrada.address.postalCode);
  if (cep && cep.length !== 8) erros["address.postalCode"] = "CEP com 8 dígitos.";
  const uf = entrada.address.state.trim();
  if (uf && !/^[A-Za-z]{2}$/u.test(uf)) erros["address.state"] = "Duas letras.";
  if (entrada.emailSignature.length > 240) erros.emailSignature = "Até 240 caracteres.";
  return erros;
}

/** Os campos numa camada só, com o endereço como `address.*`. */
function achatar(entrada: EntradaDoPerfil): Record<string, string> {
  const { address, ...resto } = entrada;
  return { ...resto, ...Object.fromEntries(Object.entries(address).map(([chave, valor]) => [`address.${chave}`, valor])) };
}

/** Quantos campos mudaram desde o gravado (a contagem da barra de salvar). */
export function camposAlterados(gravado: EntradaDoPerfil, atual: EntradaDoPerfil): number {
  const a = achatar(gravado) as Record<string, string>;
  const b = achatar(atual) as Record<string, string>;
  return Object.keys(b).filter((chave) => (a[chave] ?? "").trim() !== (b[chave] ?? "").trim()).length;
}

/** O endereço numa linha, como a API junta (`linhaDoEndereco`). */
export function linhaDoEndereco(endereco: EntradaDoPerfil["address"]): string {
  const rua = [endereco.street, endereco.number, endereco.complement].map((parte) => parte.trim()).filter(Boolean).join(", ");
  const cidade = [endereco.city.trim(), endereco.state.trim().toUpperCase()].filter(Boolean).join("/");
  const cep = so(endereco.postalCode).length === 8 ? `CEP ${mascaraDeCep(endereco.postalCode)}` : "";
  return [rua, endereco.district.trim(), cidade, cep].filter(Boolean).join(" · ");
}

/** O contato numa linha (e-mail · telefone · site). */
export function linhaDoContato(entrada: EntradaDoPerfil): string {
  return [entrada.contactEmail.trim(), entrada.phone.trim(), entrada.website.trim()].filter(Boolean).join(" · ");
}

/** O que falta, com o mesmo critério da API (`oQueFaltaNoPerfil`). */
export function oQueFalta(entrada: EntradaDoPerfil, temLogoClaro: boolean): string[] {
  const falta: string[] = [];
  if (!entrada.cnpj.trim()) falta.push("CNPJ");
  if (!entrada.contactEmail.trim() && !entrada.phone.trim()) falta.push("contato");
  if (!entrada.address.street.trim()) falta.push("endereço");
  if (!temLogoClaro) falta.push("logo");
  return falta;
}
